import { Icon } from '@/general/Icons/Icon';
import { clsx } from 'clsx';
import { FC } from 'react';

import { Image } from '@repo/ui/Image';

import { InputBaseWithOptions, InputGroupValue, InputOption, InputOptionType } from '../Input.type';
import styles from './OptionFieldInput.module.scss';

type OptionFieldInput = Prettify<
  Pick<InputOption, 'indeterminate' | 'disabled'> &
    Pick<InputBaseWithOptions<InputGroupValue>, 'disabled' | 'error'> & { checked: boolean }
>;

/**
 * Visual component representing a option field (e.g. radio or checkbox)
 *
 * **Please note:** This is purely a visual representation and should be used in conjunction of a sibling input
 */
const OptionFieldInput: FC<OptionFieldInput & { type: InputOptionType; colorSchemeGreen?: boolean }> = ({
  type,
  checked,
  disabled,
  indeterminate,
  error,
  colorSchemeGreen = false,
}) => (
  <div
    className={clsx(
      'fieldInput',
      styles.root,
      styles[type],
      {
        [styles.checked]: checked,
        [styles.indeterminate]: indeterminate,
        [styles.disabled]: disabled,
        [styles.error]: !!error,
        [styles.green]: !!colorSchemeGreen,
      },
      type,
    )}
  >
    {type === 'switch' ? (
      <div
        className={clsx(styles.thumb, {
          [styles.checked]: checked,
          [styles.disabled]: disabled,
          [styles.error]: !!error,
        })}
      />
    ) : null}
  </div>
);

/**
 * Visual component representing a radio
 *
 * **Please note:** This is purely a visual representation and should be used in conjunction of a sibling radio input
 */
export const Radio: FC<OptionFieldInput> = (props) => <OptionFieldInput {...props} type="radio" />;

/**
 * Visual component representing a checkbox
 *
 * **Please note:** This is purely a visual representation and should be used in conjunction of a sibling checkbox input
 */
export const Checkbox: FC<OptionFieldInput> = (props) => <OptionFieldInput {...props} type="checkbox" />;

/**
 * Visual component representing a switch
 *
 * **Please note:** This is purely a visual representation and should be used in conjunction of a sibling switch input
 */
export const Switch: FC<OptionFieldInput> = (props) => <OptionFieldInput {...props} type="switch" />;

/**
 * Visual component representing a checkbox as heart
 *
 * **Please note:** This is purely a visual representation and should be used in conjunction of a sibling heart checkbox input
 */
export const Heart: FC<OptionFieldInput> = ({ checked, disabled }) => (
  <div
    className={clsx(styles.heart, {
      [styles.checked]: checked,
      [styles.disabled]: disabled,
    })}
  >
    {checked ? <Icon name="heart-filled" size={18} /> : <Icon name="heart" size={18} />}
  </div>
);

/**
 * Visual component representing a checkbox as circle check
 *
 * **Please note:** This is purely a visual representation and should be used in conjunction of a sibling circle check input
 */
export const GreenRadio: FC<OptionFieldInput> = ({ checked, disabled }) => {
  if (checked) {
    return (
      <div
        className={clsx(styles.greenRadio, {
          [styles.checked]: checked,
          [styles.disabled]: disabled,
        })}
      >
        <Image src="/images/circle-check.svg" width={18} height={18} alt="Check" />
      </div>
    );
  }
  return <div className={clsx(styles.greenRadio, styles.empty)} />;
};

/**
 * Visual component representing a green checkbox
 */
export const GreenCheckbox: FC<OptionFieldInput> = (props) => (
  <OptionFieldInput {...props} type="checkbox" colorSchemeGreen />
);

/**
 * Visual component representing a green switch
 */
export const GreenSwitch: FC<OptionFieldInput> = (props) => (
  <OptionFieldInput {...props} type="switch" colorSchemeGreen />
);
